/*
 * Copyright (c) 2023-2024 AccelByte Inc. All Rights Reserved
 * This is licensed software from AccelByte Inc, for limitations
 * and restrictions contact your company contract manager.
 */

import { AccelbyteSDK, Network, SDKRequestConfig, SdkDevice, UrlHelper } from '@accelbyte/sdk'
import platform from 'platform'
import { OAuth20$, OAuth20Api } from '../all-imports'
import { CodeChallengeSentState, CodeExchangeCsrfHelpers } from './helpers/CodeExchangeCsrfHelpers'
import { CodeExchangeStateHelpers } from './helpers/CodeExchangeStateHelpers'
import { ErrorHelpers, LoginErrorExpired, LoginErrorUnknown, LoginErrorUnmatchedState } from './helpers/ErrorHelpers'

// This package is taken as-is (well, mostly anyway) from
// justice-odin/accelbyte-web-sdk/packages/sdk-iam/src/custom/clients/IamUserAuthorizationClient.ts.
const AUTHORIZE_URL = '/ic/v1/oauth/authorize'

interface UserAuthorizationOptions {
  clientId: string
  redirectURI: string
  baseURL: string
}

type FilterNullKeys<T> = {
  [K in keyof T]: NonNullable<T[K]>
}

type RequestHeaders<T> = Pick<SDKRequestConfig<T>, 'headers'>['headers']

export { LoginErrorExpired, LoginErrorUnknown, LoginErrorUnmatchedState }

export class CustomOAuthClient {
  private options: UserAuthorizationOptions

  constructor(private sdk: AccelbyteSDK) {
    const { baseURL, clientId, redirectURI } = sdk.assembly()
    this.options = {
      baseURL,
      clientId,
      redirectURI
    }
  }

  createLoginURL = (returnPath?: string | null, targetAuthPage?: string): string => {
    const { verifier, challenge } = CodeExchangeCsrfHelpers.generateChallenge()
    const csrf = CodeExchangeCsrfHelpers.generateCsrf()
    const storedState = {
      codeVerifier: verifier,
      csrf
    }
    CodeExchangeCsrfHelpers.saveCsrfToLocalStorage(storedState)

    const sentState = {
      csrf,
      payload: returnPath ? CodeExchangeStateHelpers.toJSONString({ path: returnPath }) : null
    }

    const searchParams = this.getSearchParams(sentState, challenge)
    if (targetAuthPage) {
      searchParams.target_auth_page = targetAuthPage
    }

    const effectiveBaseURL = getEffectiveBaseURL(this.options.baseURL)
    const url = new URL(UrlHelper.combineURLPaths(effectiveBaseURL, `${AUTHORIZE_URL}?${new URLSearchParams(searchParams).toString()}`))

    return url.toString()
  }

  exchangeAuthorizationCode = async ({ code, error, state }: { code?: string | null; error?: string | null; state?: string | null }) => {
    if (error) {
      throw ErrorHelpers.deduceLoginError(error)
    }
    if (!code || !state) return null

    const { result, error: stateError } = CodeExchangeCsrfHelpers.matchReceivedState(state)
    if (stateError) {
      console.error('unmatched state error: ', stateError)
      throw new LoginErrorUnmatchedState()
    }
    if (!result) return null

    const { payload, codeVerifier } = result

    const loginResult = await this.loginWithAuthorizationCode({
      code,
      codeVerifier
    })

    let returnPath: string | undefined

    if (payload) {
      const statePayload = CodeExchangeStateHelpers.readJSONString(payload)
      if (statePayload) {
        returnPath = statePayload.path
      }
    }

    return {
      response: loginResult,
      returnPath
    }
  }

  extendSession = (refreshToken: string) => {
    const headers = this.generateRequestHeaders(true)

    return OAuth20Api(this.sdk, {
      config: {
        headers
      }
    }).postOauthToken({
      grant_type: 'refresh_token',
      refresh_token: refreshToken
    })
  }

  private loginWithAuthorizationCode = async ({ code, codeVerifier }: { code: string; codeVerifier: string }) => {
    const headers = this.generateRequestHeaders()
    Network.setDeviceTokenCookie()

    const result = await OAuth20Api(this.sdk, {
      config: {
        headers
      }
    }).postOauthToken({
      grant_type: 'authorization_code',
      code,
      code_verifier: codeVerifier,
      client_id: this.options.clientId,
      redirect_uri: this.options.redirectURI
    })

    Network.removeDeviceTokenCookie()
    CodeExchangeCsrfHelpers.clearCsrfKeyInLocalStorage()
    return result
  }

  private generateRequestHeaders = (isBasicAuth: boolean = false) => {
    const deviceId = SdkDevice.getDeviceId()
    const deviceType = SdkDevice.getType()
    const headers: RequestHeaders<string> = {
      'Device-Id': deviceId,
      'Device-Type': deviceType,
      'Device-Name': platform.name ?? '',
      'Device-Os': platform.os?.toString() ?? '',
      'x-skip-access-token-proxy': 'true'
    }

    if (isBasicAuth) {
      headers.Authorization = `Basic ${btoa(this.options.clientId + ':')}`
    }

    return headers
  }

  private getSearchParams = (
    sentState: CodeChallengeSentState,
    challenge: string
  ): FilterNullKeys<Parameters<OAuth20$['getOauthAuthorize']>[0]> => {
    return {
      response_type: 'code',
      client_id: this.options.clientId,
      redirect_uri: this.options.redirectURI,
      state: JSON.stringify(sentState),
      code_challenge: challenge,
      code_challenge_method: 'S256'
    }
  }
}

// Helper functions.
function getEffectiveBaseURL(baseUrl: string) {
  let effectiveBaseURL = baseUrl

  try {
    // URL is only valid if it contains origin, so if it contains only e.g. `/web/proxy`, then new URL() will throw an error.
    effectiveBaseURL = new URL(effectiveBaseURL).href
  } catch (err) {
    if (typeof window !== 'undefined') {
      effectiveBaseURL = `${window.location.origin}${effectiveBaseURL}`
    }
  }

  return effectiveBaseURL
}
